import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { SearchFacet, SearchTabItem } from './../../../types/search';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

export const useSearch = (apiUrl: string, documentsOnly: boolean, paginationDisplayLimit: number = 10) => {
  const dateFacetKeysRef = useRef<Set<string>>(new Set());
  const [inputTerm, setInputTerm] = useState('');

  // Core search state
  const [searchParams, setSearchParams] = useState({
    term: '',
    page: 1,
    sortOrder: 'Relevance',
    activeTab: documentsOnly ? 'documents' : (null as string | null),
    filters: {} as Record<string, string[]>,
  });

  // UI state
  const [pendingFilters, setPendingFilters] = useState<Record<string, string[]>>({});
  const [openFacets, setOpenFacets] = useState<Record<string, boolean>>({});
  const [previousResults, setPreviousResults] = useState<any[]>([]);
  const [previousTabs, setPreviousTabs] = useState<any[]>([]);
  const [previousFacets, setPreviousFacets] = useState<any[]>([]);
  const [urlParamsLoaded, setUrlParamsLoaded] = useState(false);

  const buildQueryString = useCallback(
    (params: typeof searchParams, availableTabs?: SearchTabItem[], forApiCall = false) => {
      const urlParams = new URLSearchParams();

      if (params.term) urlParams.set('q', encodeURIComponent(params.term));

      const hasResults = availableTabs?.some(tab => tab.total > 0);
      const shouldIncludeParams = forApiCall || (hasResults && params.activeTab);

      if (shouldIncludeParams) {
        urlParams.set('page', params.page.toString());
        urlParams.set('order', params.sortOrder);

        let resultType = 'product';
        if (params.activeTab) {
          if (params.activeTab.toLowerCase().includes('document')) {
            resultType = 'document';
          } else if (!params.activeTab.toLowerCase().includes('product')) {
            resultType = 'content';
          }
        }
        urlParams.set('type', resultType);

        if (params.activeTab && resultType === 'content') {
          urlParams.set('pagetype', encodeURIComponent(params.activeTab));
        }

        Object.entries(params.filters).forEach(([key, values]) => {
          if (values.length > 0) {
            urlParams.set(key, values.join(','));
          }
        });
      }

      return `?${urlParams.toString()}`;
    },
    []
  );

  const updateUrl = useCallback(
    (params: typeof searchParams, availableTabs?: SearchTabItem[]) => {
      const queryString = buildQueryString(params, availableTabs);
      window.history.pushState({}, '', queryString);
    },
    [buildQueryString]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const initialParams = { ...searchParams };

    const urlSearchTerm = urlParams.get('q');
    if (urlSearchTerm) {
      const decodedTerm = decodeURIComponent(urlSearchTerm);
      initialParams.term = decodedTerm;
      setInputTerm(decodedTerm);
    }

    const urlPage = urlParams.get('page');
    if (urlPage) initialParams.page = parseInt(urlPage, 10);

    const urlSort = urlParams.get('order');
    if (urlSort) initialParams.sortOrder = urlSort;

    const urlType = urlParams.get('type');
    const urlPageType = urlParams.get('pagetype');

    if (urlType === 'document') {
      initialParams.activeTab = 'documents';
    } else if (urlPageType) {
      initialParams.activeTab = urlPageType;
    } else if (urlSearchTerm) {
      initialParams.activeTab = 'products';
    }

    if (documentsOnly) {
      initialParams.activeTab = 'documents';
    }

    const urlFilters: Record<string, string[]> = {};
    urlParams.forEach((value, key) => {
      if (!['q', 'page', 'order', 'pagetype', 'type'].includes(key)) {
        urlFilters[key] = value.split(',');
      }
    });
    initialParams.filters = urlFilters;

    setSearchParams(initialParams);
    setPendingFilters(urlFilters);
    setUrlParamsLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentsOnly]);

  const searchQuery = useQuery({
    queryKey: [
      'search',
      searchParams.term,
      {
        page: searchParams.page,
        sort: searchParams.sortOrder,
        tab: searchParams.activeTab ?? 'all',
        filters: JSON.stringify(searchParams.filters),
      },
    ],
    queryFn: async () => {
      if (!searchParams.term) {
        return {
          tabs: [],
          results: [],
          facets: [],
          pagination: { currentPage: 1, totalPages: 1 },
        };
      }

      const queryString = buildQueryString(searchParams, undefined, true); // forApiCall = true
      const apiParams = new URLSearchParams(queryString.substring(1));
      apiParams.set('pageSize', paginationDisplayLimit.toString());
      const apiQueryString = `?${apiParams.toString()}`;

      const url = `${apiUrl}${apiQueryString}`;
      const response = await fetch(url);

      if (!response.ok) throw new Error('Search failed');
      return await response.json();
    },
    staleTime: 60000,
    enabled: urlParamsLoaded && !!searchParams.term.trim(),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (searchQuery.data?.results && !searchQuery.isLoading) {
      setPreviousResults(searchQuery.data.results);
    }
    if (searchQuery.data?.tabs && !searchQuery.isLoading) {
      setPreviousTabs(searchQuery.data.tabs);
    }
    if (searchQuery.data?.facets && !searchQuery.isLoading) {
      setPreviousFacets(searchQuery.data.facets);

      // Track date facets
      const sourceFacets = searchQuery.data.facets as SearchFacet[];
      for (const f of sourceFacets) {
        if (f.type === 'date') {
          dateFacetKeysRef.current.add(f.key);
        }
      }
    }
  }, [searchQuery.data, searchQuery.isLoading]);

  useEffect(() => {
    if (searchQuery.data?.tabs?.length > 0 && searchParams.term && !searchQuery.isPlaceholderData) {
      const currentTab = searchQuery.data.tabs.find((tab: SearchTabItem) => tab.key === searchParams.activeTab);

      if ((!searchParams.activeTab || (currentTab && currentTab.total === 0)) && !documentsOnly) {
        const mainTabWithResults = searchQuery.data.tabs.find(
          (tab: SearchTabItem) => tab.total > 0 && (tab.key === 'products' || tab.key === 'documents')
        );

        if (mainTabWithResults) {
          setSearchParams(prev => {
            const newParams = { ...prev, activeTab: mainTabWithResults.key };
            updateUrl(newParams, searchQuery.data.tabs);
            return newParams;
          });
        } else {
          const contentTabWithResults = searchQuery.data.tabs.find((tab: SearchTabItem) => tab.total > 0);

          if (contentTabWithResults) {
            setSearchParams(prev => {
              const newParams = {
                ...prev,
                activeTab: contentTabWithResults.key,
              };
              updateUrl(newParams, searchQuery.data.tabs);
              return newParams;
            });
          }
        }
      }
    }
  }, [
    searchQuery.data?.tabs,
    searchParams.term,
    searchParams.activeTab,
    documentsOnly,
    updateUrl,
    searchQuery.isPlaceholderData,
    searchQuery.isLoading,
  ]);

  const facets = useMemo(() => {
    if (!searchQuery.data?.facets) return previousFacets;

    return searchQuery.data.facets
      .filter((facet: SearchFacet) => facet.key !== 'pagetype' && facet.key !== 'tab')
      .map((facet: SearchFacet) => {
        const uniqueFacets = new Map();
        (facet.items || []).forEach(item => {
          if (!uniqueFacets.has(item.value)) {
            uniqueFacets.set(item.value, {
              ...item,
              isSelected: searchParams.filters[facet.key]?.includes(item.value) || false,
            });
          }
        });

        return {
          ...facet,
          isOpen: openFacets[facet.key] || false,
          items: Array.from(uniqueFacets.values()),
        };
      });
  }, [searchQuery.data?.facets, searchParams.filters, previousFacets, openFacets]);

  useEffect(() => {
    setPendingFilters(searchParams.filters);
  }, [searchParams.activeTab, searchParams.filters]);

  const handleSearch = useCallback(() => {
    if (!inputTerm.trim()) return;

    const newParams = {
      term: inputTerm,
      page: 1,
      sortOrder: 'Relevance',
      activeTab: documentsOnly ? 'documents' : null,
      filters: {},
    };

    setSearchParams(newParams);
    setPendingFilters({});
    updateUrl(newParams, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputTerm, documentsOnly, updateUrl, searchParams]);

  const handleTabClick = useCallback(
    (tab: SearchTabItem) => {
      if (tab.key === searchParams.activeTab) return;

      let newSortOrder = searchParams.sortOrder;
      if (tab.sortOptions?.length > 0) {
        newSortOrder = tab.sortOptions[0]?.order ?? 'Relevance';
      }

      const newParams = {
        ...searchParams,
        activeTab: tab.key,
        page: 1,
        sortOrder: newSortOrder,
        filters: {},
      };

      setSearchParams(newParams);
      setPendingFilters({});
      setPreviousResults([]);
      setPreviousFacets([]);
      setOpenFacets({});
      updateUrl(newParams, searchQuery.data?.tabs);
    },
    [searchParams, updateUrl, searchQuery.data?.tabs]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      const newParams = { ...searchParams, page };
      setSearchParams(newParams);
      updateUrl(newParams, searchQuery.data?.tabs);
    },
    [searchParams, updateUrl, searchQuery.data?.tabs]
  );

  const handleSortChange = useCallback(
    (newSortOrder: string) => {
      const newParams = { ...searchParams, sortOrder: newSortOrder };
      setSearchParams(newParams);
      updateUrl(newParams, searchQuery.data?.tabs);
    },
    [searchParams, updateUrl, searchQuery.data?.tabs]
  );

  const toggleFacet = useCallback((facetKey: string) => {
    setOpenFacets(prev => {
      const isCurrentlyOpen = prev[facetKey];

      if (isCurrentlyOpen) {
        return { ...prev, [facetKey]: false };
      }

      const newState: Record<string, boolean> = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });

      newState[facetKey] = true;
      return newState;
    });
  }, []);

  const toggleFilter = useCallback((facetKey: string, itemValue: string) => {
    setPendingFilters(prev => {
      const newFilters = { ...prev };
      const values = [...(prev[facetKey] || [])];

      const index = values.indexOf(itemValue);
      if (index >= 0) {
        values.splice(index, 1);
      } else {
        values.push(itemValue);
      }

      if (values.length) {
        newFilters[facetKey] = values;
      } else {
        delete newFilters[facetKey];
      }

      return newFilters;
    });
  }, []);

  const applyFilters = useCallback(() => {
    const newParams = {
      ...searchParams,
      filters: pendingFilters,
      page: 1,
    };

    setSearchParams(newParams);
    updateUrl(newParams, searchQuery.data?.tabs);

    const activeTab = document.querySelector<HTMLElement>('.search__tab-item--selected');
    if (activeTab && window.matchMedia('(max-width: 1024px)').matches) {
      activeTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [searchParams, pendingFilters, updateUrl, searchQuery.data?.tabs]);

  const clearAllFilters = useCallback(() => {
    const newParams = {
      ...searchParams,
      filters: {},
      page: 1,
    };

    setSearchParams(newParams);
    setPendingFilters({});
    updateUrl(newParams, searchQuery.data?.tabs);
  }, [searchParams, updateUrl, searchQuery.data?.tabs]);

  const removeFilter = useCallback(
    (facetKey: string, itemValue: string) => {
      const newFilters = { ...searchParams.filters };
      if (newFilters[facetKey]) {
        const values = newFilters[facetKey].filter(v => v !== itemValue);
        if (values.length) {
          newFilters[facetKey] = values;
        } else {
          delete newFilters[facetKey];
        }
      }

      setPendingFilters(newFilters);

      const newParams = {
        ...searchParams,
        filters: newFilters,
      };

      setSearchParams(newParams);
      updateUrl(newParams, searchQuery.data?.tabs);
    },
    [searchParams, updateUrl, searchQuery.data?.tabs]
  );

  const facetsWithOpenState = useMemo(() => {
    return facets.map((facet: SearchFacet) => ({
      ...facet,
      isOpen: openFacets[facet.key] || false,
      items: (facet.items || []).map(item => ({
        ...item,
        isSelected: pendingFilters[facet.key]?.includes(item.value) || false,
      })),
    }));
  }, [facets, openFacets, pendingFilters]);

  const appliedFacetsWithState = useMemo(() => {
    return facets.map((facet: SearchFacet) => ({
      ...facet,
      items: (facet.items || []).map(item => ({
        ...item,
        isSelected: searchParams.filters[facet.key]?.includes(item.value) || false,
      })),
    }));
  }, [facets, searchParams.filters]);

  const tabs = useMemo(() => {
    if (!searchQuery.data?.tabs && previousTabs.length > 0) {
      return previousTabs.map((tab: SearchTabItem) => ({
        ...tab,
        isSelected: tab.key === searchParams.activeTab,
      }));
    }

    if (!searchQuery.data?.tabs) return [];

    return searchQuery.data.tabs.map((tab: SearchTabItem) => ({
      ...tab,
      isSelected: tab.key === searchParams.activeTab,
    }));
  }, [searchQuery.data?.tabs, searchParams.activeTab, previousTabs]);

  const filteredResults = useMemo(() => {
    if (searchQuery.isLoading && searchParams.activeTab && previousResults.length > 0) {
      return previousResults;
    }

    if (searchQuery.data?.results) {
      return searchQuery.data.results;
    }

    return [];
  }, [searchQuery.data?.results, searchQuery.isLoading, searchParams.activeTab, previousResults]);

  // Calculate if there's an unsearched term
  const hasUnsearchedTerm = useMemo(() => {
    return inputTerm.trim() !== '' && inputTerm !== searchParams.term;
  }, [inputTerm, searchParams.term]);

  // Return values for component
  return {
    // Core state and derived state
    searchTerm: inputTerm,
    currentSearchTerm: searchParams.term,
    results: filteredResults,
    activeTabKey: searchParams.activeTab,
    sortOrder: searchParams.sortOrder,

    // React Query states
    isLoading: searchQuery.isLoading,
    isError: searchQuery.isError,
    isSuccess: searchQuery.isSuccess,
    isFetching: searchQuery.isFetching,
    isPlaceholderData: searchQuery.isPlaceholderData,

    // UI state
    facets: facetsWithOpenState,
    appliedFilters: appliedFacetsWithState,
    pendingFilters,
    tabs,
    sortOptions: documentsOnly
      ? (searchQuery.data?.tabs?.[0]?.sortOptions ?? [])
      : (searchQuery.data?.tabs?.find((tab: SearchTabItem) => tab.key === searchParams.activeTab)?.sortOptions ?? []),
    pagination: {
      currentPage: searchParams.page,
      totalPages: searchQuery.data?.pagination?.totalPages ?? 1,
    },
    hasUnsearchedTerm,
    searchQuery,

    // Setters
    setSearchTerm: setInputTerm,

    // Actions
    toggleFacet,
    toggleFilter,
    applyFilters,
    removeFilter,
    clearAllFilters,
    handleTabClick,
    handleSearch,
    handlePageChange,
    handleSortChange,
  };
};
